<div class="stmt-import">

	<!-- STEP 1: pick the account, drop the file -->
	<ng-container *ngIf="mode === 'input'">
		<div class="stmt-progress" *ngIf="parsing">
			<div class="stmt-progress__text">
				<div class="stmt-progress__title">{{ parsingLabel }}</div>
			</div>
			<div class="stmt-progress__bar"><span></span></div>
		</div>

		<div class="stmt-input" [class.is-hidden]="parsing">
			<div class="stmt-input__pane stmt-input__pane--acct">
				<div class="stmt-input__label">Which account is this statement for?</div>
				<mat-form-field appearance="outline" class="full-width">
					<mat-label>ERP bank account</mat-label>
					<mat-select [(ngModel)]="bankaccount">
						<mat-option *ngFor="let b of bankaccounts" [value]="b.accountcode">
							{{ b.bankaccountname }}
						</mat-option>
					</mat-select>
				</mat-form-field>
				<div class="stmt-input__hint">
					Transactions import against this account only. Nothing posts to the general ledger.
				</div>
			</div>

			<div class="stmt-input__pane">
				<div class="stmt-input__label">Statement file</div>
				<label class="dropzone" [class.dropzone--active]="dragging" [class.dropzone--filled]="fileName"
					[class.dropzone--disabled]="!bankaccount"
					(dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
					<i class="material-icons">{{ fileName ? 'description' : (dragging ? 'file_download' : 'cloud_upload') }}</i>
					<span *ngIf="!fileName" class="dropzone__hint">
						Drop an <b>OFX/QFX</b>, <b>CSV</b>, <b>Excel</b> or <b>PDF</b> statement
						<br><span class="dropzone__sub">or click to browse</span>
					</span>
					<span *ngIf="fileName" class="dropzone__file">{{ fileName }}</span>
					<button *ngIf="fileName" type="button" mat-icon-button class="dropzone__remove"
						(click)="$event.preventDefault(); $event.stopPropagation(); clearFile()"><i class="material-icons">close</i></button>
					<input type="file" [accept]="accept" (change)="onFile($event); $event.target.value=''" hidden>
				</label>
				<div class="stmt-input__hint">
					<b>OFX/QFX is best</b> — it carries the bank's own transaction ids, so re-dropping
					an overlapping statement can never double-count.
				</div>
			</div>
		</div>

		<div class="stmt-foot" [class.is-hidden]="parsing" *ngIf="error">
			<span class="stmt-foot__err"><i class="material-icons">error_outline</i> {{ error }}</span>
		</div>

		<!-- Past drops: every prior import, with a way back in without re-finding the file -->
		<div class="stmt-history" [class.is-hidden]="parsing" *ngIf="history.length || historyLoading">
			<div class="stmt-history__head">
				<i class="material-icons">history</i>
				<span>Previously dropped</span>
				<span class="stmt-history__sub" *ngIf="historyLoading">loading…</span>
			</div>
			<div class="stmt-history__scroll">
			<table>
				<thead>
					<tr>
						<th>File</th><th>Account</th><th>Dropped</th><th>Coverage</th>
						<th class="num">New</th><th class="num">Updated</th><th></th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let d of history" [class.is-undone]="d.undone_at">
						<td class="desc" [matTooltip]="d.source_name">{{ d.source_name }}</td>
						<td>{{ bankaccountName(d.erp_bankact) }}</td>
						<td class="mono">{{ d.dropped_at | date:'M/d/yy, h:mm a' }}</td>
						<td class="mono">
							<span *ngIf="d.posted_min">{{ d.posted_min | date:'M/d/yy' }} – {{ d.posted_max | date:'M/d/yy' }}</span>
						</td>
						<td class="num mono">{{ d.imported_count }}</td>
						<td class="num mono">{{ d.updated_count }}</td>
						<td class="stmt-history__action">
							<ng-container *ngIf="!d.undone_at; else stmtUndone">
								<button mat-stroked-button *ngIf="d.has_file" [disabled]="reloadingId === d.id" (click)="reload(d)">
									<span *ngIf="reloadingId !== d.id">Reload</span>
									<span *ngIf="reloadingId === d.id">Reading…</span>
								</button>
								<span class="stmt-history__nofile" *ngIf="!d.has_file" matTooltip="This drop's file wasn't kept — only the ones dropped since this feature shipped are reloadable.">no file</span>
								<button mat-stroked-button color="warn" *ngIf="d.can_undo" [disabled]="undoingId === d.id"
									matTooltip="Remove these rows from the bank feed and un-clear anything they matched" (click)="undo(d)">
									<span *ngIf="undoingId !== d.id">Undo</span>
									<span *ngIf="undoingId === d.id">Undoing…</span>
								</button>
							</ng-container>
							<ng-template #stmtUndone><span class="stmt-history__undone">undone</span></ng-template>
						</td>
					</tr>
				</tbody>
			</table>
			</div>
		</div>
	</ng-container>

	<!-- STEP 2: review what was parsed -->
	<ng-container *ngIf="mode === 'review'">
		<div class="stmt-summary" *ngIf="preview">
			<div class="stmt-summary__totals">
				<div class="stat">
					<span class="stat__label">Parsed</span>
					<span class="stat__value">{{ rows.length }}</span>
				</div>
				<div class="stat stat--new">
					<span class="stat__label">New</span>
					<span class="stat__value">{{ preview.totals?.new || 0 }}</span>
				</div>
				<div class="stat stat--dup" *ngIf="dupRows.length">
					<span class="stat__label">Duplicates</span>
					<span class="stat__value">{{ dupRows.length }}</span>
				</div>
				<div class="stat">
					<span class="stat__label">Money in</span>
					<span class="stat__value">{{ preview.totals?.in | currency }}</span>
				</div>
				<div class="stat">
					<span class="stat__label">Money out</span>
					<span class="stat__value">{{ preview.totals?.out | currency }}</span>
				</div>
			</div>
			<div class="stmt-summary__meta">
				<span class="chip chip--fmt">{{ preview.format | uppercase }}</span>
				<span class="chip">{{ preview.bankaccountname }}</span>
				<span class="chip" *ngIf="preview.statementAccount">stmt acct {{ preview.statementAccount }}</span>
			</div>
		</div>

		<!-- Hard warnings first: wrong account, or a PDF read that doesn't balance -->
		<div class="stmt-warn stmt-warn--alert" *ngIf="preview?.accountWarning">
			<i class="material-icons">report_problem</i>
			<span>{{ preview.accountWarning }}</span>
		</div>

		<div class="stmt-warn stmt-warn--alert" *ngIf="balanceMismatch">
			<i class="material-icons">functions</i>
			<span>
				<b>This PDF may be missing transactions.</b>
				The statement's own balances move by {{ balanceCheck.expected | currency }}
				({{ balanceCheck.opening | currency }} &rarr; {{ balanceCheck.closing | currency }}), but the
				lines read add up to {{ balanceCheck.actual | currency }} — off by
				{{ balanceCheck.diff | currency }}. Check against the paper before importing, or use an
				OFX/QFX export instead.
			</span>
		</div>
		<div class="stmt-warn stmt-warn--ok" *ngIf="balanceCheck?.checked && balanceCheck?.ok">
			<i class="material-icons">check_circle</i>
			<span>Balances tie: {{ balanceCheck.opening | currency }} &rarr; {{ balanceCheck.closing | currency }} matches the lines read.</span>
		</div>

		<div class="stmt-warn" *ngIf="preview?.skipped?.length">
			<i class="material-icons">info_outline</i>
			<span>
				{{ preview.skipped.length }} row(s) skipped — no readable date or amount.
				<span class="stmt-warn__detail" *ngFor="let s of preview.skipped | slice:0:5">
					line {{ s.row }} ({{ s.reason }})<span *ngIf="!last">, </span>
				</span>
			</span>
		</div>

		<div class="stmt-warn stmt-warn--alert" *ngIf="error">
			<i class="material-icons">error_outline</i><span>{{ error }}</span>
		</div>

		<!-- CSV column mapping — only when the file is delimited -->
		<div class="stmt-map" *ngIf="needsMapping()">
			<div class="stmt-map__head">
				<i class="material-icons">view_column</i>
				<span>Column mapping</span>
				<span class="stmt-map__sub">Auto-detected. Change it if a column is wrong.</span>
			</div>
			<div class="stmt-map__grid">
				<mat-form-field appearance="outline">
					<mat-label>Date</mat-label>
					<mat-select [(ngModel)]="mapping.date">
						<mat-option value="">— none —</mat-option>
						<mat-option *ngFor="let h of headers" [value]="h">{{ h }}</mat-option>
					</mat-select>
				</mat-form-field>
				<mat-form-field appearance="outline">
					<mat-label>Amount</mat-label>
					<mat-select [(ngModel)]="mapping.amount">
						<mat-option value="">— none —</mat-option>
						<mat-option *ngFor="let h of headers" [value]="h">{{ h }}</mat-option>
					</mat-select>
				</mat-form-field>
				<mat-form-field appearance="outline">
					<mat-label>Withdrawals (if split)</mat-label>
					<mat-select [(ngModel)]="mapping.debit">
						<mat-option value="">— none —</mat-option>
						<mat-option *ngFor="let h of headers" [value]="h">{{ h }}</mat-option>
					</mat-select>
				</mat-form-field>
				<mat-form-field appearance="outline">
					<mat-label>Deposits (if split)</mat-label>
					<mat-select [(ngModel)]="mapping.credit">
						<mat-option value="">— none —</mat-option>
						<mat-option *ngFor="let h of headers" [value]="h">{{ h }}</mat-option>
					</mat-select>
				</mat-form-field>
				<mat-form-field appearance="outline">
					<mat-label>Description</mat-label>
					<mat-select [(ngModel)]="mapping.description">
						<mat-option value="">— none —</mat-option>
						<mat-option *ngFor="let h of headers" [value]="h">{{ h }}</mat-option>
					</mat-select>
				</mat-form-field>
			</div>
			<div class="stmt-map__foot">
				<mat-checkbox [(ngModel)]="mapping.invert">Flip the sign (deposits are showing as negative)</mat-checkbox>
				<mat-checkbox [(ngModel)]="remember">Remember this layout for {{ preview?.bankaccountname }}</mat-checkbox>
				<button mat-stroked-button (click)="remap()" [disabled]="parsing">
					<i class="material-icons">refresh</i> Re-read with this mapping
				</button>
			</div>
		</div>

		<!-- New rows -->
		<div class="stmt-table" *ngIf="newRows.length">
			<div class="stmt-table__head">
				<mat-checkbox [checked]="acceptedRows.length === newRows.length && newRows.length > 0"
					[indeterminate]="acceptedRows.length > 0 && acceptedRows.length < newRows.length"
					(change)="toggleAll($event.checked)">
					{{ newRows.length }} new transaction(s)
				</mat-checkbox>
				<span class="stmt-table__sel">{{ acceptedRows.length }} selected · {{ acceptedTotal | currency }}</span>
			</div>
			<table>
				<thead>
					<tr><th></th><th>Date</th><th class="num">Amount</th><th>Description</th></tr>
				</thead>
				<tbody>
					<tr *ngFor="let r of newRows">
						<td><mat-checkbox [(ngModel)]="r.accept"></mat-checkbox></td>
						<td class="mono">{{ r.postedUs }}</td>
						<td class="num mono" [class.neg]="r.amount < 0">{{ r.amount | currency }}</td>
						<td class="desc">{{ r.description }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Duplicates, collapsed. Never bulk-selectable. -->
		<div class="stmt-table stmt-table--dup" *ngIf="dupRows.length">
			<div class="stmt-table__head stmt-table__head--dup" (click)="showDups = !showDups">
				<i class="material-icons">{{ showDups ? 'expand_less' : 'expand_more' }}</i>
				<span>{{ dupRows.length }} line(s) look like duplicates — left out</span>
				<span class="stmt-table__sub">tick one only if you're sure it's a separate transaction</span>
			</div>
			<table *ngIf="showDups">
				<thead>
					<tr><th></th><th>Date</th><th class="num">Amount</th><th>Description</th><th>Why</th></tr>
				</thead>
				<tbody>
					<tr *ngFor="let r of dupRows" [class.is-exact]="r.status === 'dup_exact'">
						<td>
							<mat-checkbox [(ngModel)]="r.accept" [disabled]="r.status === 'dup_exact'"></mat-checkbox>
						</td>
						<td class="mono">{{ r.postedUs }}</td>
						<td class="num mono" [class.neg]="r.amount < 0">{{ r.amount | currency }}</td>
						<td class="desc">{{ r.description }}</td>
						<td class="why" [matTooltip]="statusHint(r.status)">{{ statusLabel(r.status) }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="stmt-foot">
			<button mat-stroked-button (click)="clearFile()">Cancel</button>
			<span class="stmt-foot__note">
				Imports into the bank feed only — matching and posting stay a separate step.
			</span>
			<button mat-raised-button color="primary" [disabled]="importing || !acceptedRows.length" (click)="commit()">
				<i class="material-icons">download</i>
				Import {{ acceptedRows.length }} transaction(s)
			</button>
		</div>
	</ng-container>

	<!-- STEP 3: done -->
	<ng-container *ngIf="mode === 'done' && result">
		<div class="stmt-done">
			<i class="material-icons">task_alt</i>
			<div class="stmt-done__text">
				<div class="stmt-done__title">{{ result.imported }} transaction(s) imported</div>
				<div class="stmt-done__sub">
					<span *ngIf="result.updated">{{ result.updated }} already present and refreshed. </span>
					<span *ngIf="result.skipped">{{ result.skipped }} skipped. </span>
					They're in the bank feed now — reconcile them on the Bank Feed tab.
				</div>
			</div>
			<button mat-stroked-button (click)="reset()">Drop another</button>
		</div>
	</ng-container>
</div>
